import { useState, useCallback } from 'react';

import {
  PaymentFormProps,
  PaymentForm,
  PaymentProvider,
  usePaymentStatus,
} from '@editor/components/Payment';
import PromoCode from '@editor/components/PromoCode/PromoCode';
import {
  FlexColumn,
  FlexContainer,
  FlexRow,
} from '@editor/components/utilities/layouts';
import { FREE_TRIAL_DAYS_AMOUNT } from '@editor/global/constants';
import useFreeTrial from '@editor/hooks/useFreeTrial';
import {
  usePlanSelectionStore,
  planSelectionStoreSelector,
} from '@editor/stores/planSelectionStore';
import { formatMonetaryValue } from '@editor/utils/billing';
import { isMobileOperatingSystem } from '@repo/detection';
import { SubscriptionPlanType } from '@repo/subscription/api';
import {
  subscriptionStoreSelector,
  useSubscriptionStore,
} from '@repo/subscription/client';
import { H3, H4, P2, P3 } from '@repo/ui/components/Typography';
import { themeStyle } from '@repo/ui/theming';

import PaySubscriptionButton from '../../ActionButtons/CreateSubscriptionActions/PaySubscriptionButton';
import useNextCharge from '../../hooks/useNextCharge';
import useSubscriptionPreview from '../../hooks/useSubscriptionPreview';
import NextPaymentSection from '../../PaymentInfo/NextPaymentSection';
import { PaymentClarification } from '../../PaymentInfo/PaymentClarification';
import usePaymentProviderData from '../hooks/usePaymentProviderData';
import usePlanPaymentDetails from '../hooks/usePlanPaymentDetails';
import usePromoCodeManagement from '../hooks/usePromoCodeManagement';
import { PlanPaymentDetailsProps } from '../PlanPurchaseDetails/types';

import NonTrialRightSection from './NonTrialRightSection';
import {
  PaymentMethodWrapper,
  PaymentMethodOption,
  PaymentMethodTitle,
  PaymentBottomSection,
  PromoCodeWrapper,
} from './styles';
import TrialRightSection from './TrialRightSection';

/**
 * This component handles the payment step of the subscription process. It displays the payment form, promo code input,
 * and next payment details. It also handles the payment process.
 */
const PaymentStep: React.FC<PlanPaymentDetailsProps> = ({
  subscriptionChangePreviewData,
  onPaymentSuccess,
  returnUrlParams,
}) => {
  const currentSubscription = useSubscriptionStore(
    subscriptionStoreSelector.subscription
  );

  const [formComplete, setFormComplete] = useState(false);
  const [paymentFormLoaded, setPaymentFormLoaded] = useState(false);

  const paymentProviderData = usePaymentProviderData({
    currentSubscription,
  });

  const { preview: changeSubscriptionPreview } = useSubscriptionPreview({
    currentSubscription,
  });
  const nextCharge = useNextCharge({
    currentSubscription,
    changeSubscriptionPreview,
  });
  const { options } = usePlanPaymentDetails(nextCharge);
  const plan = usePlanSelectionStore(planSelectionStoreSelector.plan);

  const freeTrial = useFreeTrial();
  const paymentStatus = usePaymentStatus();

  const { onPromoCodeApplication, onPromoCodeRemoval } =
    usePromoCodeManagement();

  const handlePaymentFormReady: PaymentFormProps['onPaymentReady'] =
    useCallback((): void => {
      setPaymentFormLoaded(true);
    }, []);

  const handlePaymentForChange = useCallback((complete: boolean): void => {
    setFormComplete(complete);
  }, []);

  let buttonLabel: string | undefined;
  const formattedChargeAmount = nextCharge?.value
    ? formatMonetaryValue(nextCharge?.value)
    : undefined;
  if (subscriptionChangePreviewData.isPastDue) {
    buttonLabel = `Pay Now - ${formattedChargeAmount}`;
  }

  const nonMobileRightSection = freeTrial?.isAvailable ? (
    <TrialRightSection plan={plan} startDate={nextCharge?.date || new Date()} />
  ) : (
    <NonTrialRightSection plan={plan} />
  );

  let paymentSectionMaxHeight;

  if (isMobileOperatingSystem()) {
    paymentSectionMaxHeight = '400px';
  } else {
    paymentSectionMaxHeight = freeTrial?.isAvailable ? '315px' : '410px';
  }
  return (
    <PaymentProvider {...paymentProviderData}>
      <FlexContainer height="100%">
        <FlexColumn width={isMobileOperatingSystem() ? '100%' : 'unset'}>
          <PaymentMethodWrapper>
            <FlexColumn
              data-testid="subscriptionModal--payment-step-heading"
              gap="24px"
            >
              {freeTrial?.isAvailable ? (
                <FlexColumn gap="12px">
                  <H3>
                    <b>
                      Try {plan === SubscriptionPlanType.PRO ? 'Pro' : 'Expert'}{' '}
                      For Free
                    </b>
                  </H3>
                  <P2>
                    {FREE_TRIAL_DAYS_AMOUNT} days for free, cancel any moment.
                  </P2>
                </FlexColumn>
              ) : (
                <H3>
                  <b>
                    Get Kittl{' '}
                    {plan === SubscriptionPlanType.PRO ? 'Pro' : 'Expert'}
                  </b>
                </H3>
              )}
              <PaymentMethodOption
                style={{
                  maxHeight: paymentSectionMaxHeight,
                }}
              >
                <PaymentMethodTitle>Payment Method</PaymentMethodTitle>
                <PaymentForm
                  isDefaultCollapsed={false}
                  options={options}
                  onChange={handlePaymentForChange}
                  onPaymentReady={handlePaymentFormReady}
                />
              </PaymentMethodOption>
            </FlexColumn>
          </PaymentMethodWrapper>

          {/* Payment bottom section, containing promo code, next charge details and payment button */}
          <PaymentBottomSection gap="16px">
            <PromoCodeWrapper>
              <PromoCode
                disabled={paymentStatus.loading}
                onApplyClick={onPromoCodeApplication}
                onRemoveClick={onPromoCodeRemoval}
              />
            </PromoCodeWrapper>
            <PaymentClarification nextCharge={nextCharge} />
            {nextCharge && (
              <FlexColumn gap="16px">
                {freeTrial?.isAvailable ? (
                  <>
                    <FlexRow justifyContent="space-between">
                      <P3 color={themeStyle.varInkMedium}>
                        {nextCharge.label}
                      </P3>
                      <P3 color={themeStyle.varInkMedium}>
                        {formatMonetaryValue(nextCharge.value)}
                      </P3>
                    </FlexRow>
                    <FlexRow justifyContent="space-between">
                      <P3>
                        {nextCharge.note?.title} ({FREE_TRIAL_DAYS_AMOUNT} days
                        free)
                      </P3>
                      <H4>
                        {formatMonetaryValue(
                          {
                            ...nextCharge.value,
                            amount: 0,
                          },
                          { fractionDigits: 0 }
                        )}
                      </H4>
                    </FlexRow>
                  </>
                ) : (
                  <NextPaymentSection nextCharge={nextCharge} />
                )}
              </FlexColumn>
            )}
            <PaySubscriptionButton
              dataTestId="subscriptionModal--payment-step-button"
              disabled={!formComplete || !paymentFormLoaded}
              label={buttonLabel}
              nextChargeDate={nextCharge?.date || new Date()}
              pastDue={subscriptionChangePreviewData.isPastDue}
              returnUrlParams={returnUrlParams}
              onSuccess={onPaymentSuccess}
            />
          </PaymentBottomSection>
        </FlexColumn>
        {!isMobileOperatingSystem() && nonMobileRightSection}
      </FlexContainer>
    </PaymentProvider>
  );
};

export default PaymentStep;
